import { Box, Button, Container, Stack, SvgIcon, Typography, alpha, useTheme } from '@mui/material';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { useNavigate, useLocation } from 'react-router-dom';
import vmIcon from '@/assets/vm-icon.png';
import { routePaths } from '@/app/router/routePaths';
import { UserMenu } from './UserMenu';

const iconPaths = {
  wave: 'M4 10h2v8H4v-8zm4-4h2v12H8V6zm4-3h2v15h-2V3zm4 6h2v9h-2V9zm4-4h2v13h-2V5z',
  music: 'M12 3v11.55A4 4 0 1 0 14 18V7h6V3h-8zM8 20a2 2 0 1 1 0-4 2 2 0 0 1 0 4z',
  headphones: 'M12 3a8 8 0 0 0-8 8v6a3 3 0 0 0 3 3h2v-8H6v-1a6 6 0 0 1 12 0v1h-3v8h2a3 3 0 0 0 3-3v-6a8 8 0 0 0-8-8z',
  plus: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z',
};

const waveformBars = [22, 38, 16, 44, 28, 52, 18, 34, 48, 26, 42, 20, 36];

const navLinks = [
  { label: 'Dashboard', path: routePaths.djDashboard, icon: iconPaths.wave },
  { label: 'Events', path: routePaths.djEvents, icon: iconPaths.music },
  { label: 'Spotify', path: routePaths.djSpotify, icon: iconPaths.headphones },
];

function NavIcon({ path, ...props }: SvgIconProps & { path: string }) {
  return (
    <SvgIcon fontSize="small" {...props}>
      <path d={path} />
    </SvgIcon>
  );
}

export function DjHeader() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const renderNavLink = (link: (typeof navLinks)[number], compact = false) => {
    const active =
      link.path === routePaths.djDashboard
        ? location.pathname === link.path
        : location.pathname.startsWith(link.path);

    return (
      <Button
        key={link.path}
        onClick={() => navigate(link.path)}
        startIcon={<NavIcon path={link.icon} />}
        size="small"
        sx={{
          position: 'relative',
          minHeight: compact ? 36 : 40,
          px: compact ? 1.15 : 1.55,
          py: 0.75,
          color: active ? 'common.white' : alpha(theme.palette.common.white, 0.68),
          bgcolor: active ? alpha(theme.palette.common.white, 0.085) : 'transparent',
          borderRadius: 99,
          fontWeight: active ? 900 : 700,
          fontSize: compact ? 13 : 14,
          letterSpacing: 0,
          whiteSpace: 'nowrap',
          border: `1px solid ${active ? alpha(theme.palette.common.white, 0.12) : 'transparent'}`,
          transition: 'background-color 150ms ease, color 150ms ease, border-color 150ms ease',
          '&:hover': {
            color: 'common.white',
            bgcolor: alpha(theme.palette.common.white, 0.08),
            borderColor: alpha(theme.palette.common.white, 0.1),
          },
          '& .MuiButton-startIcon': { mr: 0.75 },
          ...(active && {
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&::after': {
              content: '""',
              position: 'absolute',
              left: '20%',
              right: '20%',
              bottom: 4,
              height: 3,
              borderRadius: 99,
              background: `linear-gradient(90deg, ${theme.palette.secondary.light}, ${theme.palette.warning.light})`,
            },
          }),
        }}
      >
        {link.label}
      </Button>
    );
  };

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.appBar,
        overflow: 'hidden',
        pt: { xs: 0.75, sm: 1 },
        backgroundImage: `
          linear-gradient(180deg, ${alpha(theme.palette.text.primary, 0.8)} 0%, ${alpha(theme.palette.text.primary, 0.46)} 60%, ${alpha(theme.palette.text.primary, 0)} 100%),
          linear-gradient(118deg, ${alpha(theme.palette.primary.dark, 0.52)} 0 24%, transparent 24% 46%, ${alpha(theme.palette.secondary.dark, 0.32)} 46% 64%, transparent 64%)
        `,
        backdropFilter: 'blur(20px)',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `linear-gradient(90deg, transparent 0%, ${alpha(theme.palette.secondary.light, 0.2)} 22%, ${alpha(theme.palette.warning.light, 0.16)} 48%, ${alpha(theme.palette.primary.light, 0.14)} 68%, transparent 92%)`,
          transform: 'skewY(-1.6deg) translateY(-60%)',
          transformOrigin: 'left top',
          height: 54,
        },
      }}
    >
      <Container maxWidth="lg">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            position: 'relative',
            py: { xs: 0.6, sm: 0.75 },
            gap: { xs: 1.25, sm: 2 },
            borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.065)}`,
          }}
        >
          {/* Logo */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ minWidth: 0, flexShrink: 0 }}
          >
            <Box
              component="img"
              src={vmIcon}
              alt=""
              aria-hidden="true"
              sx={{
                width: { xs: 34, sm: 38 },
                height: { xs: 34, sm: 38 },
                borderRadius: '34% 46% 36% 44%',
                display: 'block',
                transform: 'rotate(-3deg)',
                boxShadow: `0 0 0 1px ${alpha(theme.palette.common.white, 0.12)}, 0 7px 16px ${alpha(theme.palette.secondary.main, 0.16)}`,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="p"
                sx={{
                  fontSize: { xs: 16, sm: 18 },
                  lineHeight: 1,
                  fontWeight: 900,
                  color: 'common.white',
                  letterSpacing: 0,
                }}
              >
                Vibe Masters
              </Typography>
              <Typography
                component="p"
                sx={{
                  mt: 0.4,
                  color: alpha(theme.palette.common.white, 0.55),
                  fontSize: 11,
                  lineHeight: 1,
                  fontWeight: 650,
                  textTransform: 'uppercase',
                }}
              >
                DJ Console
              </Typography>
            </Box>
          </Stack>

          {/* Nav links */}
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{
              display: { xs: 'none', md: 'flex' },
              flex: 1,
              justifyContent: 'center',
            }}
          >
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                p: 0.25,
                borderRadius: 99,
                bgcolor: alpha(theme.palette.common.black, 0.08),
              }}
            >
              {navLinks.map((link) => renderNavLink(link))}
            </Stack>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
            <UserMenu tone="dark" />
          </Stack>
        </Stack>

        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            overflowX: 'auto',
            gap: 0.75,
            py: 0.8,
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {navLinks.map((link) => renderNavLink(link, true))}
        </Box>
      </Container>

      {/* Waveform accent */}
      <Box
        aria-hidden="true"
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '3px',
          height: 6,
          maxWidth: 1200,
          mx: 'auto',
          px: { xs: 3, sm: 4 },
          pb: 0,
          overflow: 'hidden',
          opacity: 0.14,
        }}
      >
        {waveformBars.map((h, i) => (
          <Box
            key={i}
            sx={{
              flex: '0 0 auto',
              width: 3,
              height: h * 0.22,
              borderRadius: '2px 2px 0 0',
              background:
                i % 3 === 0
                  ? `linear-gradient(180deg, ${theme.palette.warning.light}, ${theme.palette.secondary.main})`
                  : `linear-gradient(180deg, ${theme.palette.secondary.light}, ${theme.palette.primary.light})`,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
